import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatINRFull } from "@/lib/format";
import { generateLoanFilePDF } from "@/lib/loanFilePdf";
import { X, FileText, Download, ChevronRight } from "lucide-react";

function Field({ label, value }) {
  return (
    <div className="py-2.5 border-b border-slate-100 last:border-0">
      <div className="text-xs uppercase tracking-wide text-slate-400 font-semibold mb-1">{label}</div>
      <div className="text-sm font-semibold text-[#1a1a1b] break-words">{value === "" || value == null ? "—" : String(value)}</div>
    </div>
  );
}

function DocLink({ name, url, signed }) {
  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid="doc-link"
      className="flex items-center justify-between gap-2 px-3 py-2 rounded-sm border border-slate-200 hover:border-[#b3122a] hover:bg-[#b3122a]/5 transition text-sm">
      <span className="flex items-center gap-2 text-[#1a1a1b] font-medium truncate"><FileText size={14} className="text-[#b3122a] flex-shrink-0" />{name}</span>
      <span className="flex items-center gap-2 flex-shrink-0">
        {signed !== undefined && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${signed ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{signed ? "SIGNED" : "UNSIGNED"}</span>}
        <Download size={14} className="text-slate-400" />
      </span>
    </a>
  );
}

function KV({ rows }) {
  return <div className="grid grid-cols-2 gap-x-6">{rows.map((r, i) => <Field key={i} label={r.label} value={r.value} />)}</div>;
}

export function DynamicSection({ s }) {
  const empty =
    (s.type === "records" && !(s.records && s.records.length)) ||
    (s.type === "documents" && !(s.documents && s.documents.length)) ||
    (s.type === "table" && !(s.rows && s.rows.length)) ||
    (s.type === "kv" && !(s.rows && s.rows.length) && !(s.documents && s.documents.length));
  const meta = s.type === "records" ? `${(s.records || []).length} record(s)`
    : s.type === "documents" ? `${(s.documents || []).length} file(s)`
    : s.type === "table" ? `${(s.rows || []).length} row(s)` : "";
  return (
    <details className="rf-card !p-0 overflow-hidden group" data-testid={`section-${s.key}`} open={!empty && s.key !== "applicant"}>
      <summary className="cursor-pointer select-none px-5 py-4 flex items-center justify-between font-display font-bold text-[#1a1a1b]">
        <span>{s.title}{!empty && meta && <span className="ml-2 text-xs font-semibold text-slate-400">{meta}</span>}</span>
        <ChevronRight size={16} className="text-slate-400 group-open:rotate-90 transition-transform" />
      </summary>
      <div className="px-5 pb-5">
        {empty ? <p className="text-sm text-slate-400 italic" data-testid={`section-${s.key}-empty`}>Not available</p> : (
          <>
            {s.type === "kv" && <><KV rows={s.rows} />{s.documents && s.documents.length > 0 && <div className="grid sm:grid-cols-2 gap-2 mt-3">{s.documents.map((dd, i) => <DocLink key={i} name={dd.label} url={dd.url} />)}</div>}</>}
            {s.type === "documents" && <div className="grid sm:grid-cols-2 gap-2">{s.documents.map((dd, i) => <DocLink key={i} name={dd.name} url={dd.url} signed={dd.signed} />)}</div>}
            {s.type === "records" && <div className="space-y-4">{s.records.map((rec, i) => <div key={i} className="border border-slate-100 rounded-sm p-3"><div className="text-xs font-bold text-[#b3122a] mb-1">#{i + 1}</div><KV rows={rec.rows} />{rec.documents && rec.documents.length > 0 && <div className="grid sm:grid-cols-2 gap-2 mt-2">{rec.documents.map((dd, j) => <DocLink key={j} name={dd.label} url={dd.url} />)}</div>}</div>)}</div>}
            {s.type === "table" && <div className="overflow-x-auto"><table className="w-full text-xs"><thead><tr className="text-left text-slate-400 border-b border-slate-100">{s.columns.map((c) => <th key={c} className="py-2 px-2 whitespace-nowrap">{c}</th>)}</tr></thead><tbody>{s.rows.map((r, i) => <tr key={i} className="border-b border-slate-50">{s.columns.map((c) => <td key={c} className="py-1.5 px-2 whitespace-nowrap">{r[c]}</td>)}</tr>)}</tbody></table></div>}
          </>
        )}
      </div>
    </details>
  );
}

export function LoanFileDrawer({ id, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/approved/${id}`).then((r) => setD(r.data)).catch(() => {}); }, [id]);
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="loan-file-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-3xl bg-[#F4F1EA] h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-[#111111] text-white px-6 py-5 flex items-start justify-between z-10">
          <div>
            <div className="font-mono text-xs text-slate-400">Loan No: {d?.loan_no || d?.applicant_no || "…"}</div>
            <h2 className="font-display text-xl font-extrabold mt-1">{d?.company || "Loading…"}</h2>
            {d && <div className="text-sm text-slate-300 mt-1">{formatINRFull(d.loan_amount)} · {d.loan_type} · {d.tenor} mo · {d.status || d.app_type}</div>}
          </div>
          <button onClick={onClose} data-testid="close-detail" className="text-slate-300 hover:text-white"><X size={22} /></button>
        </div>
        {!d ? <div className="p-8 text-slate-400">Loading full loan file…</div> : (
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs text-slate-400 flex-1 min-w-[200px]">All fields & linked records — live from the loan management system. Empty sections show "Not available".</p>
              <button
                onClick={() => generateLoanFilePDF(d)}
                data-testid="download-loan-pdf"
                className="flex items-center gap-2 px-4 py-2 rounded-sm bg-[#b3122a] text-white text-sm font-semibold hover:bg-[#940f23] transition flex-shrink-0">
                <Download size={15} /> Download Full Loan File (PDF)
              </button>
            </div>
            {d.sections && d.sections.map((s) => <DynamicSection key={s.key} s={s} />)}
          </div>
        )}
      </div>
    </div>
  );
}
